import React from 'react';
import { cn } from '../../../components/ui/_cn';

// The page shell every other screen in this dashboard writes inline —
// finance.js, stores.js, customers.js, bank-accounts.js and
// account-activity.js all repeat the same two divs. The finance section has a
// layout route, so it can hold the shell in one place instead: the gutter, the
// column and the rhythm between blocks live here, and each page renders its
// own content.
//
// Background and min-height are deliberately NOT here — FinanceSection paints
// those once, around the whole section. A page that falls back to a legacy
// screen (Overview and Bank Account both do) renders that screen bare, because
// those components still carry their own full shell.
//
// Gutters are the mock's 32/40/44 (spec 2026-09-22 §3.2), replacing round 1's
// 24/32. `column` replaced `wide`: the money pages (Overview, Payouts, Order
// Reports, Payout detail) fill the screen up to the mock's own 1360px outer
// frame (round 2, spec 2026-09-22 §D — "full" is capped, not unbounded), and
// the reading pages (Business Details, Tax Documents) sit in the mock's 920px
// column. Disputes uses the full column too: Stripe's disputes table needs
// the width (QA, 2026-09-25). 20px between blocks — the mock's rhythm, shared
// by every page the shell serves.
const COLUMNS = {
  full: 'max-w-[1360px] mx-auto',
  reading: 'max-w-[920px] mx-auto',
};

// Stripe's banner is drawn by the section, above the page and outside this
// shell, and has to stand in the frame of the page under it (2026-10-06: it
// ran the whole width of a wide screen over a 1360px Overview). The section
// knows the page by its route segment, so the reading pages are named here,
// beside the widths.
//
// Not every page under the banner uses this shell. A company not yet on
// direct charges gets the legacy Finance screen at the index and the legacy
// Payments screen at bank-account, and both bring their own shell: a 1024px
// column behind a 32px gutter. The order list has that gutter and no column
// at all. The banner takes each of those frames too, or it would overhang a
// legacy page the way it overhung Overview.
const READING_PAGES = ['business-details', 'tax-documents'];
const SHELL_GUTTER = 'px-4 pt-6 sm:px-6 lg:px-10 lg:pt-8';
const LEGACY_GUTTER = 'px-4 pt-6 sm:px-6 lg:px-8 lg:pt-8';

export const bannerFrame = (segment, { legacyModel = false, orderList = false } = {}) => {
  if (READING_PAGES.includes(segment)) return { gutter: SHELL_GUTTER, column: COLUMNS.reading };
  if (orderList) return { gutter: LEGACY_GUTTER, column: '' };
  if (segment === 'bank-account' || (segment === '' && legacyModel)) {
    return { gutter: LEGACY_GUTTER, column: 'max-w-5xl mx-auto' };
  }
  return { gutter: SHELL_GUTTER, column: COLUMNS.full };
};

const FinancePageShell = ({ column = 'full', children }) => (
  <div className="px-4 pt-6 pb-8 sm:px-6 lg:px-10 lg:pt-8 lg:pb-11">
    <div className={cn('space-y-5', COLUMNS[column] || COLUMNS.full)}>
      {children}
    </div>
  </div>
);

export default FinancePageShell;
